import {
  Button,
  FlexContainer,
  FormBuilder,
  IconLibrary,
} from '@act/storybook';
import { GoalsSidePanel } from './GoalsSidePanel';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  CreateNeedFormDto,
  CreateNeedFormSchema,
  FilterOptionDto,
  NeedDto,
  NeedsCategoryDto,
  NeedsIssueDto,
  SuggestedNeedDto,
  UpdateNeedFormDto,
  UpdateNeedFormSchema,
} from '@act/shared/data-transfer-objects';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import {
  createNeedSuccess,
  metaSliceSelectors,
  patchNeedSuccess,
  selectNeedsCategoryByIdFromMetadata,
  selectNeedsIssueByIdFromMetadata,
  selectNeedsMetadata,
  selectNeedsZcodeByIdFromMetadata,
  selectSelectedMemberId,
  suggestedNeedUpdated,
} from 'redux/redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useToast } from '@act/react-components/app/ReusableToast';
import ZCodeContent from '@act/react-components/dialog-forms/learn-more-zcode-content';
import StyledAlert from '@act/toolkit/alerts/styled-alert';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useApiCall } from 'redux/utils/api-hook';
import { useForm } from 'react-hook-form';
import { LearnMoreContent } from './LearnMoreContent';
import { useTracking } from '@act/react-components/tracking';

interface AddNeedContentProps {
  open: boolean;
  onClose: () => void;
  need?: NeedDto;
  suggestedNeed?: SuggestedNeedDto;
}

export const AddNeedContent = ({
  open,
  onClose,
  need,
  suggestedNeed,
}: AddNeedContentProps) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { generateTrackingProps } = useTracking();
  const needsMetadata = useAppSelector(selectNeedsMetadata);
  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();
  const [openLearnMoreContent, setOpenLearnMoreContent] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<NeedsCategoryDto>(null);
  const [selectedIssue, setSelectedIssue] = useState<NeedsIssueDto>(null);
  const [createdNeedResult, _isCreatingNeed, createNeed] = useApiCall(
    needRequests.createNeed
  );
  const [updateNeedResult, _patchLoading, patchNeed] = useApiCall(
    needRequests.patchNeed
  );

  const { control, watch, formState, reset, setValue, handleSubmit } = useForm<
    CreateNeedFormDto | UpdateNeedFormDto
  >({
    mode: 'onChange',
    resolver: zodResolver(!!need ? UpdateNeedFormSchema : CreateNeedFormSchema),
    defaultValues: {
      issueId: need?.issueId ?? suggestedNeed?.issueId ?? 0,
      reason: need?.reason ?? suggestedNeed?.reason ?? '<p><br></p>',
    },
  });
  const { isDirty } = formState;
  const watchIssue = watch('issueId');

  const selectedIssueZCode = selectNeedsZcodeByIdFromMetadata(
    needsMetadata?.data,
    selectedIssue?.zcodeId
  );

  const formConfig: FormConfig<CreateNeedFormDto | UpdateNeedFormDto> = useMemo(
    () => [
      {
        type: 'input',
        name: 'issueId',
        label: 'Search for need',
        input: 'autocomplete',
        props: {
          options: needIssueOptions,
          getOptionLabel: (option: FilterOptionDto) => option?.label || '',
          required: true,
          groupBy: (option: FilterOptionDto) => option?.tagCloud[0],
          startAdornment: (
            <IconLibrary
              iconName="MagnifyingGlass"
              size={18}
              weight="regular"
            />
          ),
          trackingName: 'create-need-dialog-issue-autocomplete',
          sensitive: false,
        },
      },
      {
        type: 'passthrough' as const,
        content: selectedCategory ? (
          <Box>
            <StyledAlert
              severity="info"
              title={selectedCategory?.label}
              message={`This issue has a target time window of ≤ ${selectedIssue?.defaultTimeFrameInDays} days.`}
              actions={[
                <Button
                  key="extend-time"
                  onClick={() => setOpenLearnMoreContent(true)}
                  {...generateTrackingProps('open-learn-more-content-button')}
                >
                  Learn more
                </Button>,
              ]}
            />
            <Divider sx={{ margin: '16px 0' }} />
          </Box>
        ) : null,
      },
      {
        type: 'passthrough' as const,
        content: (
          <Typography
            variant="titleMedium"
            sx={{ fontWeight: 500, fontSize: '16px' }}
          >
            What is the reason for pursuing this need?*
          </Typography>
        ),
      },
      {
        type: 'input',
        name: 'reason',
        label: 'Reason for pursuing this need*',
        input: 'richText',
        props: {
          placeholderText: 'Reason for pursuing this need*',
          height: 346,
          required: true,
          defaultValue: need?.reason ?? '<p><br></p>',
        },
      },
    ],
    [
      needIssueOptions,
      selectedCategory,
      selectedIssue,
      selectedIssueZCode,
      openLearnMoreContent,
    ]
  );

  useEffect(() => {
    if (watchIssue) {
      const issue = selectNeedsIssueByIdFromMetadata(
        needsMetadata?.data,
        watchIssue
      );
      const category = selectNeedsCategoryByIdFromMetadata(
        needsMetadata?.data,
        issue?.categoryId
      );
      setSelectedIssue(issue);
      setSelectedCategory(category);
    }
  }, [watchIssue]);
  useEffect(() => {
    if (!!need) {
      setValue('issueId', need.issueId);
      setValue('reason', need.reason);
    } else if (suggestedNeed) {
      setValue('issueId', suggestedNeed.issueId);
      setValue('reason', suggestedNeed.reason);
    } else {
      setValue('issueId', 0);
      setValue('reason', '<p><br></p>');
    }
  }, [need, suggestedNeed, setValue]);

  useEffect(() => {
    if (createdNeedResult || updateNeedResult) {
      if (suggestedNeed) {
        dispatch(
          suggestedNeedUpdated({
            callId: suggestedNeed.callId,
            tempNeedId: suggestedNeed?.tempNeedId,
            createdNeed: createdNeedResult,
          })
        );
      }
      if (createdNeedResult) {
        dispatch(createNeedSuccess(createdNeedResult));
        toast('Need created successfully');
      }
      if (updateNeedResult) {
        dispatch(patchNeedSuccess(updateNeedResult));
        toast('Need updated successfully');
      }
      onClose();
    }
  }, [createdNeedResult, updateNeedResult]);

  async function handleSubmission(data: CreateNeedFormDto | UpdateNeedFormDto) {
    if (need) {
      patchNeed({
        body: {
          issueId: data.issueId,
          reason: data.reason,
        },
        params: { needId: need.id },
      });
    } else {
      createNeed({
        body: {
          memberId: selectedMemberId,
          issueId: data.issueId,
          reason: data.reason,
          ...(!!suggestedNeed &&
            !!suggestedNeed.tempNeedId && {
              tempNeedId: suggestedNeed.tempNeedId,
            }),
        },
        params: null,
      });
    }
  }
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  return (
    <>
      {openLearnMoreContent ? (
        <LearnMoreContent
          open={openLearnMoreContent}
          setOpen={setOpenLearnMoreContent}
          selectedIssueZCode={<ZCodeContent zcode={selectedIssueZCode} />}
          selectedCategory={selectedCategory}
        />
      ) : (
        <GoalsSidePanel
          open={open}
          animated={false}
          titleContent={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: '16px',
              }}
            >
              <IconButton onClick={handleClose}>
                <IconLibrary iconName="ArrowLeft" size={32} weight="regular" />
              </IconButton>
              <Typography sx={{ alignSelf: 'center' }} variant="titleLarge">
                {!!need ? 'Edit Need' : 'Add New Need'}
              </Typography>
            </Box>
          }
          content={
            <FlexContainer direction="column" gap={2}>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </FlexContainer>
          }
          footerContent={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                gap: 2,
              }}
            >
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                {...generateTrackingProps(
                  !!need
                    ? 'update-need-from-call-submit-button'
                    : 'add-new-need-from-call-submit-button'
                )}
                onClick={handleSubmit(handleSubmission)}
              >
                submit
              </Button>
            </Box>
          }
        />
      )}
    </>
  );
};
